import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import UnifiedModal from '../UnifiedModal';
import { pushEvent, ANALYTICS_EVENTS } from '../../../utils/analytics';
import { setAgentHandoff } from '../../../utils/aiAgentHandoff';
import { navigateToCheckoutOverlay } from '../../../utils/checkoutOverlayNavigation';
import UpgradeOfferSummary from '../../UpgradeOfferSummary';
import './PaywallModal.css';

/**
 * Paywall Modal - Shown when users reach their limits or lack access
 *
 * Displays usage info and prompts user to upgrade to a paid plan.
 * Handles different limit types (message, backtest, strategy, bot) with
 * appropriate messaging for each.
 *
 * Key behaviors:
 * - limit=0: Shows "no access" message (feature not included in plan)
 * - limit>0: Shows "quota exhausted" message (used all X this month)
 * - Hides usage stats when limit=0 (they're meaningless)
 * - Shows actual reset date instead of relative time
 */
const PaywallModal = ({
  isOpen,
  onClose,
  message: customMessage, // Optional override for the message
  limitType = 'message', // 'message' | 'backtest' | 'backtest_minutes' | 'strategy' | 'bot'
  usage,
  checkoutUrl = '/pricing',
  upgradeOffer = null,
  // List of the user's currently running deployments. When populated, the modal
  // switches from "buy a plan" mode to "stop an existing bot" mode. This fixes
  // the case where a Starter-plan user hits their 1-bot limit and the UI tells
  // them to buy the Starter plan they already have. Shape per entry:
  //   { deployId, botId, botName, strategyName }
  activeDeployments = [],
  // Conversation context - used to return user to their conversation after checkout
  conversationId = null,
  aiStrategyId = null,
  ...props
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasTrackedOpen = useRef(false);
  const resolvedCheckoutUrl =
    upgradeOffer?.checkoutUrl || upgradeOffer?.contactSalesUrl || checkoutUrl || '/pricing';
  const upgradeLabel = upgradeOffer?.terminal
    ? 'Contact sales'
    : upgradeOffer?.productName
      ? `Upgrade to ${upgradeOffer.productName}`
      : 'View plans';

  // Track when paywall is shown (only once per modal open)
  useEffect(() => {
    if (isOpen && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      pushEvent({
        event: ANALYTICS_EVENTS.AI_AGENT_MESSAGE_LIMIT_REACHED.name,
        limitType,
        used: usage?.used,
        limit: usage?.limit,
        remaining: usage?.remaining,
        resetWindowDays: usage?.resetWindowDays,
        resetsAt: usage?.resetsAt,
      });
    }
    if (!isOpen) {
      hasTrackedOpen.current = false;
    }
  }, [isOpen, usage, limitType]);

  const handleGetStarterPlan = () => {
    // Track CTA click
    pushEvent({
      event: ANALYTICS_EVENTS.AI_AGENT_PAYWALL_CTA_CLICKED.name,
      cta_type: 'get_starter_plan',
      limitType,
      checkoutUrl: resolvedCheckoutUrl,
      usage: usage ? { used: usage.used, limit: usage.limit } : undefined,
      conversationId, // Track which conversation user was on
    });

    // Save conversation context so user returns to their conversation after checkout
    // This prevents the redirect loop where users end up on Getting Started instead
    if (conversationId || aiStrategyId) {
      setAgentHandoff({
        conversationId,
        aiStrategyId,
        source: 'paywall_upgrade',
      });
      console.log('[PaywallModal] Saved handoff for post-checkout return:', { conversationId, aiStrategyId });
    }

    onClose();
    navigateToCheckoutOverlay(navigate, resolvedCheckoutUrl, location);
  };

  const handleDismiss = () => {
    // Track dismissal
    pushEvent({
      event: ANALYTICS_EVENTS.AI_AGENT_PAYWALL_DISMISSED.name,
      limitType,
      usage: usage ? { used: usage.used, limit: usage.limit } : undefined,
    });
    onClose();
  };

  const handleViewUsage = () => {
    onClose();
    navigate('/account-settings/usage');
  };

  // Format reset date as "Dec 28" instead of "in X days"
  const formatResetDate = (resetsAt) => {
    if (!resetsAt) return null;
    const resetDate = new Date(resetsAt);
    return resetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get dynamic message based on limit type and whether limit is 0
  const getMessage = () => {
    // Use custom message if provided
    if (customMessage) return customMessage;

    const limit = usage?.limit ?? 0;

    if (!Number.isFinite(limit) || limit < 0) {
      return 'Your account usage could not be resolved. View Usage for the current limits and refresh before trying again.';
    }

    // limit=0 means the feature is not included in their plan at all
    if (limit === 0) {
      switch (limitType) {
        case 'backtest':
          return 'Backtesting is not included in your current plan. Upgrade to unlock backtesting capabilities and test your strategies.';
        case 'backtest_minutes':
          return 'Backtest runtime is not included in your current plan. Upgrade to unlock backtesting capabilities and test your strategies.';
        case 'strategy':
          return 'Strategy building is not included in your current plan. Upgrade to start creating AI-powered trading strategies.';
        case 'bot':
          return 'Bot deployment is not included in your current plan. Upgrade to deploy your strategies to live trading.';
        case 'message':
        default:
          return 'AI Agent access is not included in your current plan. Upgrade to start building your trading strategy.';
      }
    }

    // limit>0 means they had quota but exhausted it
    // Handle singular (limit=1) vs plural (limit>1) with proper grammar
    // NOTE: No plan offers "unlimited" anything - all plans have limits
    switch (limitType) {
      case 'backtest':
        return limit === 1
          ? `You've used your monthly backtest. Upgrade for more backtests.`
          : `You've used all ${limit} backtests this month. Upgrade for more backtests.`;
      case 'backtest_minutes':
        return limit === 1
          ? `You've used your 1 backtest minute this month. Upgrade for more backtesting.`
          : `You've used all ${limit} backtest minutes this month. Upgrade for more backtesting.`;
      case 'strategy':
        return limit === 1
          ? `You've used your monthly strategy revision. Upgrade for more revisions.`
          : `You've used all ${limit} strategy revisions this month. Upgrade for more revisions.`;
      case 'bot':
        return limit === 1
          ? `You've reached your bot deployment limit. Upgrade for more deployments.`
          : `You've reached your limit of ${limit} deployed bots. Upgrade for more deployments.`;
      case 'message':
      default:
        return limit === 1
          ? `You've used your free message. Upgrade for more messages.`
          : `You've used all ${limit} free messages. Upgrade for more messages.`;
    }
  };

  // Get the appropriate title
  const getTitle = () => {
    const limit = usage?.limit ?? 0;
    if (!Number.isFinite(limit) || limit < 0) {
      return 'Usage Unavailable';
    }
    if (limit === 0) {
      return 'Upgrade Required';
    }
    return 'Upgrade to Continue';
  };

  // Get the aria label based on limit type
  const getAriaLabel = () => {
    const limit = usage?.limit ?? 0;
    if (!Number.isFinite(limit) || limit < 0) {
      return 'Account usage unavailable';
    }
    if (limit === 0) {
      return `${limitType} access requires upgrade`;
    }
    return `${limitType} limit reached`;
  };

  const resetDateText = formatResetDate(usage?.resetsAt);
  const limit = usage?.limit ?? 0;
  const hasQuota = limit > 0;
  const hasInvalidLimit = !Number.isFinite(limit) || limit < 0;

  // "Already on a paid plan and at bot limit" mode: the user has running bots
  // and needs to stop one (or upgrade), not buy the plan they already have.
  const isBotLimitWithActiveDeployments =
    limitType === 'bot' && Array.isArray(activeDeployments) && activeDeployments.length > 0;

  const handleGoToMyBots = () => {
    pushEvent({
      event: ANALYTICS_EVENTS.AI_AGENT_PAYWALL_CTA_CLICKED.name,
      cta_type: 'go_to_my_bots',
      limitType,
      usage: usage ? { used: usage.used, limit: usage.limit } : undefined,
      activeDeploymentCount: activeDeployments.length,
    });
    onClose();
    navigate('/my-strategies');
  };

  if (isBotLimitWithActiveDeployments) {
    const botPlural = activeDeployments.length === 1 ? 'bot' : 'bots';
    return (
      <UnifiedModal
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        className="paywall-modal paywall-modal--bot-limit"
        ariaLabel="Bot limit reached"
        {...props}
      >
        <div className="paywall-modal-content">
          <h2 className="paywall-modal-title">Stop a bot to deploy a new one</h2>

          <p className="paywall-modal-message">
            Your plan allows {limit} {limit === 1 ? 'bot' : 'bots'} running at a time, and you already have {activeDeployments.length} active {botPlural}:
          </p>

          <ul className="paywall-modal-bot-list">
            {activeDeployments.map((d) => (
              <li key={d.deployId || d.botId} className="paywall-modal-bot-list-item">
                <span className="paywall-modal-bot-name">{d.botName || d.strategyName || 'Unnamed bot'}</span>
                {d.strategyName && d.botName && d.strategyName !== d.botName ? (
                  <span className="paywall-modal-bot-strategy"> ({d.strategyName})</span>
                ) : null}
              </li>
            ))}
          </ul>

          <p className="paywall-modal-message">
            Stop one of your running bots to free up a slot, or upgrade your plan for more deployments.
          </p>

          <div className="paywall-modal-actions">
            <button
              className="paywall-btn paywall-btn-primary paywall-btn-orange"
              onClick={handleGoToMyBots}
              data-testid="paywall-go-to-my-bots-btn"
            >
              Go to My Bots
            </button>
            {!upgradeOffer ? <button
              className="paywall-btn paywall-btn-secondary"
              onClick={handleGetStarterPlan}
              data-testid="paywall-upgrade-plan-btn"
            >
              Upgrade Plan
            </button> : null}
          </div>
          {upgradeOffer ? <UpgradeOfferSummary
            offer={upgradeOffer}
            reason="Review the included capacity before changing plans."
            onUpgrade={handleGetStarterPlan}
            actionTestId="paywall-upgrade-plan-btn"
          /> : null}
        </div>
      </UnifiedModal>
    );
  }

  if (hasInvalidLimit) {
    return (
      <UnifiedModal
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        className="paywall-modal"
        ariaLabel={getAriaLabel()}
        {...props}
      >
        <div className="paywall-modal-content">
          <h2 className="paywall-modal-title">{getTitle()}</h2>
          <p className="paywall-modal-message">{getMessage()}</p>
          <div className="paywall-modal-actions">
            <button
              className="paywall-btn paywall-btn-primary"
              onClick={handleViewUsage}
              data-testid="paywall-view-usage-btn"
            >
              View Usage
            </button>
            <button
              className="paywall-btn paywall-btn-secondary"
              onClick={handleDismiss}
              data-testid="paywall-dismiss-btn"
            >
              Close
            </button>
          </div>
        </div>
      </UnifiedModal>
    );
  }

  if (upgradeOffer) {
    return (
      <UnifiedModal isOpen={isOpen} onClose={onClose} size="md" className="paywall-modal paywall-modal--offer" ariaLabel={getAriaLabel()} {...props}>
        <UpgradeOfferSummary
          offer={upgradeOffer}
          reason={upgradeOffer.terminal ? 'You’ve reached a limit on your current plan.' : getMessage()}
          onUpgrade={handleGetStarterPlan}
          actionTestId="paywall-get-starter-btn"
          secondaryAction={<button type="button" className="paywall-offer-dismiss" onClick={handleDismiss}>Not now</button>}
        />
      </UnifiedModal>
    );
  }

  return (
    <UnifiedModal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      className="paywall-modal"
      ariaLabel={getAriaLabel()}
      {...props}
    >
      <div className="paywall-modal-content">
        <div className="paywall-modal-icon">
          {/* Rocket icon - represents "upgrade", "level up", aspirational growth */}
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
            <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
            <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
            <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
          </svg>
        </div>

        <h2 className="paywall-modal-title">{getTitle()}</h2>

        <p className="paywall-modal-message">
          {getMessage()}
        </p>

        {/* Only show usage stats if limit > 0 (they're meaningless for limit=0) */}
        {usage && hasQuota && (
          <div className="paywall-modal-usage">
            <div className="usage-stat">
              <span className="usage-value">{usage.used}{limitType === 'backtest_minutes' ? ' min' : ''}</span>
              <span className="usage-label">Used</span>
            </div>
            <div className="usage-divider">/</div>
            <div className="usage-stat">
              <span className="usage-value">{usage.limit}{limitType === 'backtest_minutes' ? ' min' : ''}</span>
              <span className="usage-label">Limit</span>
            </div>
            {resetDateText && (
              <div className="usage-reset">
                Resets {resetDateText}
              </div>
            )}
          </div>
        )}

        <div className="paywall-modal-actions">
          <button
            className="paywall-btn paywall-btn-primary paywall-btn-orange"
            onClick={handleGetStarterPlan}
            data-testid="paywall-get-starter-btn"
          >
            {upgradeLabel}
          </button>
          <button
            className="paywall-btn paywall-btn-secondary"
            onClick={handleDismiss}
            data-testid="paywall-dismiss-btn"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </UnifiedModal>
  );
};

export default PaywallModal;
